"use client";

// useLockFold — the DETACHED fold (law_scroll_lock, revised 2026-09-23).
//
// A pinned head used to collapse by animating `max-height` from its natural
// height to zero, half a second after a scroll threshold, once the scroll had
// rested. Every frame of that transition relaid out the whole document (the
// content below rose by exactly the folded height), the threshold + the rest
// beat made it feel late, and it only re-opened at the very top of the page.
// That is the "criminal stutter": two motions at once, both of them on the
// layout thread, arriving after the gesture that asked for them.
//
// Now the head's LAYOUT BOX NEVER CHANGES. The descriptive matter (`.os-lock__fold`)
// fades and lifts on the compositor, and what follows it in the head (the view
// chips, the document tabs) rides up by its height — transforms only, nothing
// under the head moves. The fold is written here as ONE number, `--lock-fold`
// (0 = open, 1 = folded), on the head element itself:
//
//   · IT COMMITS. Past a short scroll (FOLD_ON) the head folds — all the way,
//     at once — and it opens again the same way on the way back (FOLD_OFF, with
//     hysteresis so a scroll resting on the line can't strobe it). John on the
//     first, scroll-linked version: "states where it is not fully one or the
//     other" — a head that can be half-open reads as undecided. The number is
//     only ever 0 or 1; the confidence is in the CSS transition on the head
//     (a fast spring, a whisper of blur as the matter goes), not in tracking.
//     No timer: the flip is on the scroll frame that crosses the line.
//   · HOVER OPENS IT (on a real mouse move, never mid-scroll: see below).
//     Bring a mouse over a folded head and it unfolds OVER the
//     content (`data-expand`), the band growing with it; leave and it folds
//     back to where the scroll says. Nothing beneath scrolls or moves. Mouse
//     only — a finger has no hover, and a tap must not flash the head open.
//     ONLY THE VISIBLE HEAD opens it (law_mapped_controls, 2026-09-28): folded,
//     the head's box still stands at its open height, so the CSS lets that box
//     pass the pointer and only what is seen (the ticker, the chips) takes it —
//     pointerenter reaches this element through them. A mouse over the empty
//     stretch where the matter used to be reaches the content, not the head.
//   · `data-folded` flips with it, so a folded head's matter takes no clicks
//     (its opacity is 0; it must not eat the tap meant for the content).
//
// The head opts in with the `os-lock--detached` class; the CSS for that class
// is what turns the number into motion.

import { useEffect, type RefObject } from "react";
import { stageScroller, takeNudge } from "../stage/scroller";
import { revealTracker } from "../stage/reveal";

// px of scroll that folds the head, and where it opens again (hysteresis)
export const FOLD_ON = 28;
const FOLD_OFF = 8;

export function useLockFold(
  head: RefObject<HTMLElement | null>,
  opts?: {
    /** the element that scrolls (default: the stage's scroller when a stage
     *  stands — lib/stage/scroller — else the window). */
    scroller?: RefObject<HTMLElement | null>;
    /** px of scroll that folds the head (default 28; opens again under 8). */
    distance?: number;
    /** false = leave the head exactly as it is (the fold is never written). */
    active?: boolean;
    /** THE STAGE (components/stage): the head really shrinks when it folds, and
     *  the content area grows by the same amount. Folding is then only allowed
     *  when the content would still be scrolled past the line afterwards, or the
     *  grown area would clamp the scroll back under it and the head would open
     *  again, and fold again: a flap. */
    room?: boolean;
    /** false = a mouse over the folded head does not open it (the reader: its
     *  folded chips and tabs are what you came for, and opening the head under
     *  the pointer moves them away from it; a scroll up brings it back). */
    hover?: boolean;
  },
): void {
  const scroller = opts?.scroller;
  const distance = opts?.distance;
  const active = opts?.active !== false;
  const room = !!opts?.room;
  const hover = opts?.hover !== false;
  useEffect(() => {
    const el = head.current;
    if (!el || !active) return;
    const target: HTMLElement | Window = scroller?.current ?? stageScroller() ?? window;
    let frame = 0;
    let folded: boolean | null = null;
    const scrollTop = () => (target instanceof Window ? target.scrollY : target.scrollTop);
    const on = distance ?? FOLD_ON;
    const off = Math.min(FOLD_OFF, on / 2);
    // how much shorter the head gets when it folds (published on it by useNaturalHeight)
    // (read from the computed style: a split stage's head is not detached, and
    // its fold's height is published on the stage root above it; a split head
    // also gives back the rise to where it pins, a registered length in px)
    const depth = () => {
      const cs = getComputedStyle(el);
      const px = (name: string) => parseFloat(cs.getPropertyValue(name)) || 0;
      return px("--lock-h") + px("--lock-gap") + px("--stage-pin-lift");
    };
    // what the position alone says (past the line, with hysteresis), and the
    // scroll up that opens it anyway (lib/stage/reveal)
    let past: boolean | null = null;
    const reveal = revealTracker();
    const write = () => {
      frame = 0;
      const y = scrollTop();
      let nextPast = past ? y > off : y >= on;
      if (nextPast && !past && room && !(target instanceof Window)) {
        const max = target.scrollHeight - target.clientHeight;
        if (max - depth() < on) nextPast = false;
      }
      past = nextPast;
      // the stage's own moves (holding the page still through a split fold) are
      // not the reader's scroll up or down
      const revealed = reveal(y, nextPast, target instanceof Window ? 0 : takeNudge(target));
      const next = nextPast && !revealed;
      el.toggleAttribute("data-revealed", revealed);
      if (next === folded) return;
      folded = next;
      el.style.setProperty("--lock-fold", next ? "1" : "0");
      el.toggleAttribute("data-folded", next);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(write); };
    // HOVER, ONLY ON PURPOSE (2026-10-08, the navigator's flash). A browser
    // sends pointer events to whatever comes to rest under a still mouse while
    // the page scrolls, and the folded head's hit area changes as its chips
    // ride up: every scroll near the top opened and shut the head, each time
    // replaying the fold. So the head opens only on a REAL movement of the
    // mouse over it (a synthetic move has no movement), never while a scroll is
    // in flight, and only after a short intent pause; leaving closes at once.
    let scrolling = false;
    let scrollIdle = 0;
    let intent = 0;
    const SCROLL_IDLE_MS = 180;
    const INTENT_MS = 110;
    const onScroll = () => {
      scrolling = true;
      window.clearTimeout(scrollIdle);
      scrollIdle = window.setTimeout(() => { scrolling = false; }, SCROLL_IDLE_MS);
      if (intent) { window.clearTimeout(intent); intent = 0; }
      schedule();
    };
    write();
    target.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", schedule);
    const move = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" || scrolling || intent || el.hasAttribute("data-expand")) return;
      if (e.movementX === 0 && e.movementY === 0) return;
      intent = window.setTimeout(() => {
        intent = 0;
        if (!scrolling) el.setAttribute("data-expand", "");
      }, INTENT_MS);
    };
    const out = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      if (intent) { window.clearTimeout(intent); intent = 0; }
      el.removeAttribute("data-expand");
    };
    if (hover) {
      el.addEventListener("pointermove", move);
      el.addEventListener("pointerleave", out);
    } else {
      el.removeAttribute("data-expand");
    }
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.clearTimeout(scrollIdle);
      window.clearTimeout(intent);
      target.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", schedule);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerleave", out);
      el.style.removeProperty("--lock-fold");
      el.removeAttribute("data-folded");
      el.removeAttribute("data-revealed");
      el.removeAttribute("data-expand");
    };
  }, [head, scroller, distance, active, room, hover]);
}
